import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useAccount } from '../../context/AccountContext';
import { showInkToast } from '../cms/InkToast';
import { KEY_HELD_TOAST } from './voiceWords';
import { GET_MEDIA_ASSETS, GET_MEDIA_VIDEO } from '../../graphql';
import { currentUrl, type LibraryAsset } from '../content/MediaUrlField';
import { uploadAsset } from '../../lib/media/uploadAsset';
import {
  calendarDate,
  Channel,
  channelKind,
  channelMark,
  channelName,
  CREATE_REACH_POST,
  DISCARD_POST_DRAFT,
  isPersonalProfile,
  zoneCity,
  REACH_CHANNELS,
  ReachPost,
  SCHEDULE_POST_DRAFT,
  shortDate,
  toDate,
  UPDATE_POST_DRAFT,
  UPDATE_PENDING_POST,
  postText,
  offSiteOf,
  REACH_SITE_HOST,
  type LinkTarget,
  PUBLISHING_OFF_WORDS,
  publishingOff,
  SOCIAL_NETWORK_RULES,
} from './reach';
import { LinkPicker } from './LinkPicker';
import { PostPreview } from './PostPreview';
import { useLinkedMedia } from '../media/LinkedMedia';
import { useMediaAllowance } from '../media/MediaAllowance';
import {
  blockingMessage,
  channelProblem,
  joinNames,
  KIND_WORDS,
  kindOfPost,
  POST_KINDS,
  takes,
  unsupportedNote,
  videoCheck,
  type NetworkRule,
  type PostKind,
} from './composerRules';
import { VideoField, videoFromLibrary, type ComposerVideo } from './VideoField';
import { fitText, lengthTone, networkKey, postLength, postLimit } from './postLength';
import { askInk } from '../cms/InkConfirm';

/**
 * Reach → Calendar → "Write a post": a person posts by hand. One post per
 * channel picked, through the same post service every other producer uses;
 * the person is their own approver, so it goes straight on the calendar.
 *
 * It starts on "Save as draft" (drafts list on the Calendar), so nothing goes
 * out on one click: "Post now", and anything bound for a personal profile, asks
 * first, naming where it goes.
 *
 * From Prototype v14: a character count per network picked under the text, and
 * a version of the text for a network it's too long for; the image from Media,
 * an upload (which lands in Media too) or an address; and the campaign the post
 * belongs to.
 *
 * Post as (Composer Post Types and Video spec): the person says what the post
 * is, and every channel picked is checked against it. Photo, video and link are
 * held apart until it's saved, so switching never loses any; only the chosen
 * kind's go out. Saving keeps the kind: it's never worked out from what's attached.
 *
 * Link (feedback 3dc485ba): search the site's pages and articles or paste an
 * address. Picking one makes a link preview with the page's own image; to show a
 * different one, the post is a Photo.
 */

export type When = 'now' | 'later' | 'draft';

/** The post's picture; `auto` when it came with the link that was picked. */
export interface PostImage {
  url: string;
  name: string;
  from: string;
  auto?: boolean;
}

const LINKED_FROM = 'From what you linked · change it any time';

/** The picture a picked page or article brings, if it has one. */
export const imageFor = (t: Pick<LinkTarget, 'kind' | 'title' | 'imageUrl'>): PostImage | null =>
  t.imageUrl
    ? {
        url: t.imageUrl,
        name: `${t.kind === 'page' ? 'Share image' : 'Main image'} · ${t.title}`,
        from: LINKED_FROM,
        auto: true,
      }
    : null;

const localInputValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** Dated campaigns a post can be filed under: not ended. */
const WRITE_POST_CAMPAIGNS = gql`
  query WritePostCampaigns($accountId: ID!) {
    projects(accountId: $accountId) {
      id
      name
      kind
      status
      deadline
      startDate
      createdAt
      channels {
        id
      }
    }
  }
`;

const MEDIA_ASSET_URL = gql`
  query WritePostAsset($accountId: ID!, $id: ID!) {
    mediaAsset(accountId: $accountId, id: $id) {
      id
      sha256
      originalFilename
      renditions {
        kind
        url
        isCurrent
      }
    }
  }
`;

export interface PostCampaign {
  id: string;
  name: string;
  kind: string;
  status: string;
  deadline?: string | null;
  startDate?: string | null;
  createdAt?: string | null;
  channels?: Array<{ id: string }>;
}

// Start and end are calendar days (stored as midnight UTC), so read them as dates.
const campaignWindow = (c: PostCampaign) => {
  const start = calendarDate(c.startDate) || toDate(c.createdAt);
  const end = calendarDate(c.deadline);
  if (end) end.setHours(23, 59, 59, 999);
  return { start, end };
};

const TONE_COLOR = { over: 'var(--ink-red)', near: 'var(--ink-amber)', ok: 'var(--ink-ink3)' };

export function WritePost({
  onClose,
  onPosted,
  campaignId: initialCampaign = '',
  draft = null,
  link: initialLink = '',
  linkTarget: initialTarget = null,
  text: initialText = '',
  image: initialImage = null,
  when: initialWhen = 'draft',
  at: initialAt = null,
  channelIds = null,
  approver = null,
  channelList = null,
  campaignList = null,
  variant = 'modal',
}: {
  onClose: () => void;
  onPosted: () => void;
  /** Opened from a campaign: filed under it, on its channels. */
  campaignId?: string;
  /**
   * Drafts → Finish (Prototype v14 "Draft"): the draft, to finish, schedule or
   * keep. A scheduled, waiting or missed post (Calendar → post → Edit,
   * Prototype v15) opens the same way to change its words, link and picture;
   * it keeps its channel, time and approval.
   */
  draft?: ReachPost | null;
  /** Calendar → Share a page (Prototype v15): the page's address, filled in. */
  link?: string;
  /** What `link` is on the site (the article screen's "Write a post about this"). */
  linkTarget?: Pick<LinkTarget, 'kind' | 'title'> | null;
  /** Words to start from (the article's summary). */
  text?: string;
  /** A picture to start with (the article's main image, `auto`). */
  image?: PostImage | null;
  when?: When;
  /** The time "Pick a time" starts on (an article that isn't live yet: after it is). */
  at?: Date | null;
  /** Today → Write a fresh post (Prototype v16): the missed post's channel, picked. */
  channelIds?: string[] | null;
  /**
   * The Campaign role (campaign board, 2026-10-23): who approves ("Hai").
   * Their post never goes out on its own: a time is proposed and it waits for
   * that person; no "Now". They pick and upload images in Media like anyone.
   */
  approver?: string | null;
  /** The channels to offer, when the caller has them (the Campaign role can't list them). */
  channelList?: Channel[] | null;
  /** The campaigns to offer, likewise. */
  campaignList?: PostCampaign[] | null;
  /**
   * modal: New post, with channels and side-by-side previews. panel: inside the
   * Reach side panel, editing a post already on the calendar or a campaign
   * board — its fields and buttons only (the channel can't change).
   */
  variant?: 'modal' | 'panel';
}) {
  const forApproval = approver !== null;
  const { selectedAccount, accountId } = useAccount();
  const apollo = useApolloClient();
  const { data } = useQuery<{ socialProfiles: Channel[] }>(REACH_CHANNELS, {
    variables: { accountId },
    skip: !accountId || Boolean(channelList),
  });
  const { data: campData } = useQuery<{ projects: PostCampaign[] }>(WRITE_POST_CAMPAIGNS, {
    variables: { accountId },
    skip: !accountId || Boolean(campaignList),
    fetchPolicy: 'cache-and-network',
  });
  const channels = useMemo(
    () => (channelList || data?.socialProfiles || []).filter((c) => c.isActive),
    [data, channelList]
  );
  const campaigns = useMemo(() => {
    const now = Date.now();
    return (campaignList || campData?.projects || []).filter((c) => {
      if (c.kind === 'always_on' || ['completed', 'cancelled'].includes(c.status)) return false;
      const { end } = campaignWindow(c);
      return !end || end.getTime() >= now;
    });
  }, [campData, campaignList]);

  const [picked, setPicked] = useState<Set<string> | null>(() =>
    draft ? new Set([draft.socialProfileId]) : channelIds?.length ? new Set(channelIds) : null
  );
  const [campaignId, setCampaignId] = useState(draft?.projectId || initialCampaign);
  const [text, setText] = useState(() => (draft ? postText(draft) : initialText));
  // Per network, a version of the text that fits ("Write an X version").
  const [versions, setVersions] = useState<Record<string, string>>({});
  const [link, setLink] = useState(draft?.postUrl || initialLink);
  const [target, setTarget] = useState<Pick<LinkTarget, 'kind' | 'title'> | null>(() =>
    draft ? null : initialTarget
  );
  // Round 9: a link off the site sends readers away; say so as it's pasted.
  const { data: hostData } = useQuery<{ reachSiteHost: string | null }>(REACH_SITE_HOST, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const offSite = offSiteOf(link, hostData?.reachSiteHost);
  const [image, setImage] = useState<PostImage | null>(() =>
    draft?.imageUrl
      ? {
          url: draft.imageUrl,
          name: draft.imageUrl.split('/').pop() || 'Image',
          from: 'On the draft',
        }
      : draft
        ? null
        : initialImage
  );
  const pickLink = (t: LinkTarget) => {
    setLink(t.url);
    setTarget(t);
  };
  // What the post is: nothing until the person says, on a new post.
  const [kind, setKind] = useState<PostKind | ''>(() => (draft ? kindOfPost(draft) : ''));
  const [video, setVideo] = useState<ComposerVideo | null>(() =>
    draft && !draft.mediaAssetId && kindOfPost(draft) === 'video' && draft.imageUrl
      ? {
          key: `url-${draft.id}`,
          assetId: null,
          name: draft.imageUrl.split('/').pop() || 'Video',
          status: 'ready',
          pct: 100,
          durationSeconds: draft.channelSettings?.media?.durationSeconds ?? null,
          width: draft.channelSettings?.media?.width ?? null,
          height: draft.channelSettings?.media?.height ?? null,
          sizeBytes: null,
          src: draft.imageUrl,
          url: draft.imageUrl,
          posterUrl: null,
          info: null,
        }
      : null
  );
  // Reopening a video post brings its video back as it is now.
  const { data: ownVideo } = useQuery(GET_MEDIA_VIDEO, {
    variables: { accountId, assetId: draft?.mediaAssetId },
    skip: !accountId || !draft?.mediaAssetId || kindOfPost(draft || {}) !== 'video',
    fetchPolicy: 'network-only',
  });
  const ownVideoLoaded = useRef(false);
  useEffect(() => {
    const found = ownVideo?.mediaVideo;
    if (!found?.video || ownVideoLoaded.current) return;
    ownVideoLoaded.current = true;
    setVideo((was) => was || videoFromLibrary({ ...found, bytes: found.video.sizeBytes ?? null }));
  }, [ownVideo]);
  const [youtubeTitle, setYoutubeTitle] = useState(() =>
    draft && networkKey(draft.platform) === 'youtube' ? draft.title || '' : ''
  );
  const ownTiktok = draft?.channelSettings?.tiktok;
  const [tiktok, setTiktok] = useState({
    privacy: ownTiktok?.privacy || '',
    allowComments: ownTiktok?.allowComments ?? true,
    allowDuet: ownTiktok?.allowDuet ?? false,
    allowStitch: ownTiktok?.allowStitch ?? false,
  });
  const { data: rulesData } = useQuery<{ socialNetworkRules: NetworkRule[] }>(
    SOCIAL_NETWORK_RULES,
    {
      fetchPolicy: 'cache-first',
    }
  );
  const rulesFor = (network: string) =>
    rulesData?.socialNetworkRules?.find((r) => r.platform === network) || null;
  const { allowance, refetch: refetchAllowance } = useMediaAllowance(accountId);
  // Minutes the plan can still add (a video already in Media has been counted).
  const minutesLeft =
    allowance?.videoLimitSeconds == null
      ? null
      : Math.max(
          0,
          Math.floor((allowance.videoLimitSeconds - (allowance.videoUsedSeconds || 0)) / 60)
        );
  const [imageMode, setImageMode] = useState<'none' | 'url' | 'library'>('none');
  const [imageUrl, setImageUrl] = useState('');
  const [libQ, setLibQ] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  // An edit starts on the post's own time (UX optimization review 2026-10-24:
  // Edit post has When, like Write a post).
  // Times arrive as epoch milliseconds or ISO (toDate reads both).
  const editTime =
    draft && draft.reachStatus !== 'draft'
      ? toDate(draft.scheduleTime) || toDate(draft.proposedTime)
      : null;
  const [when, setWhen] = useState<When>(editTime ? 'later' : initialWhen);
  const [at, setAt] = useState(() => {
    const start = editTime || initialAt;
    return localInputValue(
      start && start.getTime() > Date.now() ? start : new Date(Date.now() + 60 * 60 * 1000)
    );
  });
  const [busy, setBusy] = useState(false);
  const [createPost] = useMutation(CREATE_REACH_POST);
  const [updateDraft] = useMutation(UPDATE_POST_DRAFT);
  const [scheduleDraft] = useMutation(SCHEDULE_POST_DRAFT);
  const [discardDraft] = useMutation(DISCARD_POST_DRAFT);
  const [updatePending] = useMutation(UPDATE_PENDING_POST);
  // Not a draft: an edit of a post already on its way (it keeps its time).
  const editing = Boolean(draft && draft.reachStatus !== 'draft');
  // Edit post's subtitle: "Threads · Monte Carlo · from Share new articles".
  const editChannel = editing ? channels.find((c) => c.id === draft?.socialProfileId) : undefined;
  const fromRule = editing
    ? (campData?.projects || []).find((c) => c.id === draft?.projectId && c.kind === 'always_on')
        ?.name || null
    : null;
  const { data: libData, loading: libLoading } = useQuery<{
    mediaAssets: { assets: LibraryAsset[] };
  }>(GET_MEDIA_ASSETS, {
    variables: { accountId, limit: 60, offset: 0 },
    skip: !accountId || imageMode !== 'library',
    fetchPolicy: 'cache-and-network',
  });

  // Opened from a campaign, its channels start picked.
  const campaignChannels = useMemo(
    () =>
      new Set(
        (campaigns.find((c) => c.id === initialCampaign)?.channels || [])
          .map((c) => c.id)
          .filter((id) => channels.some((ch) => ch.id === id))
      ),
    [campaigns, channels, initialCampaign]
  );
  const chosen = picked ?? campaignChannels;

  const toggle = (id: string) =>
    setPicked(() => {
      const next = new Set(chosen);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const going = channels.filter((c) => chosen.has(c.id));
  const networks = [...new Set(going.map((c) => networkKey(c.platform)))];
  const textFor = (key: string) => versions[key] ?? text;
  const isVideo = kind === 'video';
  const usesLink = kind === 'photo' || kind === 'video' || kind === 'link';
  const sentLink = usesLink ? link : '';
  const lengthContext = {
    media: kind === 'photo' || kind === 'video',
    campaign: selectedAccount?.utmCampaign,
  };
  const lengthOf = (key: string) => postLength(key, textFor(key), sentLink, lengthContext);
  const unsupported = networks.filter((k) => !takes(rulesFor(k), kind));
  const fine = networks.filter((k) => !unsupported.includes(k));
  const tooLong = fine.filter((k) => {
    const lim = postLimit(k);
    return lim != null && lengthOf(k) > lim;
  });
  // A rejected video isn't one the channels need to hear about.
  const checkable = isVideo && video && video.status !== 'rejected' ? video : null;
  const checks = checkable
    ? fine.map((k) => ({
        key: k,
        name: channelName(k),
        ...videoCheck(rulesFor(k), checkable),
      }))
    : [];
  const videoFails = checks.filter((c) => c.state === 'fail').map((c) => c.key);
  const leaveOut = (keys: string[]) => {
    setPicked(
      new Set(going.filter((c) => !keys.includes(networkKey(c.platform))).map((c) => c.id))
    );
    showInkToast(`${joinNames(keys.map(channelName))} left out of this post.`);
  };
  const typeFix = unsupportedNote(unsupported.map((k) => ({ key: k, name: channelName(k) })));
  const needsTitle = isVideo && networks.includes('youtube');
  const needsTiktok = isVideo && networks.includes('tiktok');
  const tiktokChannel = going.find((c) => networkKey(c.platform) === 'tiktok');
  const laterOk = when !== 'later' || new Date(at).getTime() > Date.now();
  const goesOut = when !== 'draft';
  const block = blockingMessage({
    channelCount: chosen.size,
    kind,
    unsupported: unsupported.map(channelName),
    hasLink: Boolean(link.trim()),
    hasPhoto: Boolean(image),
    hasVideo: Boolean(video),
    videoRejected: video?.status === 'rejected',
    videoFails: videoFails.map(channelName),
    hasText: Boolean(text.trim()),
    tooLong: tooLong.map(channelName),
    needsYoutubeTitle: needsTitle && !youtubeTitle.trim(),
    needsTiktokPrivacy: needsTiktok && !tiktok.privacy,
    timeOk: laterOk,
    goesOut,
  });
  const videoBusy = isVideo && (video?.status === 'uploading' || video?.status === 'processing');
  // The upload has to have started before anything can wait for it.
  const canPost =
    !block && !busy && !uploading && !(isVideo && video && !video.assetId && !video.url);
  const footNote =
    block ||
    (videoBusy && goesOut && !editing
      ? when === 'now'
        ? 'The video is still preparing. It posts the moment it’s ready.'
        : 'The video is still preparing. It posts at this time, once it’s ready.'
      : editing && kind
        ? `Saving keeps this a ${KIND_WORDS[kind]}.`
        : '');
  // What the chosen kind sends, and only that: the others are held until it's saved.
  const postMedia = () => ({
    mediaUrl:
      kind === 'photo'
        ? image?.url || null
        : isVideo && !video?.assetId
          ? video?.url || null
          : null,
    mediaAssetId: isVideo ? video?.assetId || null : null,
  });
  const titleFor = (network: string, body: string) =>
    isVideo && network === 'youtube' ? youtubeTitle.trim() : body;
  const tiktokFor = (network: string) =>
    isVideo && network === 'tiktok' && tiktok.privacy ? tiktok : null;

  const campaign = campaigns.find((c) => c.id === campaignId) || null;
  const campaignHint = (() => {
    if (!campaign || when !== 'later') return '';
    const { start, end } = campaignWindow(campaign);
    const t = new Date(at).getTime();
    if ((start && t < start.getTime()) || (end && t > end.getTime())) {
      return `Outside ${campaign.name} (${shortDate(start)} – ${shortDate(end)}). It still posts, but won’t count toward the campaign’s results.`;
    }
    return '';
  })();

  const pickAsset = (a: LibraryAsset, from: string) => {
    const url = currentUrl(a);
    if (!url) {
      setImageError('That image isn’t ready yet. Try another, or try again in a moment.');
      return;
    }
    setImage({ url, name: a.originalFilename || 'Image', from });
    setImageMode('none');
    setImageError('');
  };

  const upload = async (file: File | undefined) => {
    if (!file || !accountId) return;
    setUploading(true);
    setImageError('');
    try {
      const { assetId } = await uploadAsset(accountId, file, { filename: file.name });
      const { data: got } = await apollo.query<{ mediaAsset: LibraryAsset | null }>({
        query: MEDIA_ASSET_URL,
        variables: { accountId, id: assetId },
        fetchPolicy: 'network-only',
      });
      if (got?.mediaAsset) pickAsset(got.mediaAsset, 'Uploaded · added to Media');
      else setImageError('Uploaded, but the image couldn’t be read back. Choose it from Media.');
    } catch (err) {
      setImageError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  /** Edit: the post's own row takes the new words, link and picture; nothing else changes. */
  const saveEdit = async () => {
    if (!draft || !canPost) return;
    const network = networkKey(going[0]?.platform || draft.platform);
    const body = textFor(network).trim();
    setBusy(true);
    try {
      const { data: saved } = await updatePending({
        variables: {
          id: draft.id,
          input: {
            text: body,
            title: titleFor(network, body),
            linkUrl: sentLink.trim() || null,
            ...postMedia(),
            tiktok: tiktokFor(network),
            postType: kind,
            // Only when changed: an always-on campaign isn't in the list and stays.
            ...(campaignId !== (draft.projectId || '') ? { campaignId: campaignId || null } : {}),
            // When: a person's save puts it on the calendar (and approves a
            // waiting post); a Campaign-role person's proposes the time.
            scheduleTime:
              when === 'draft' ? null : when === 'now' ? 'now' : new Date(at).toISOString(),
          },
        },
      });
      const whenWords =
        when === 'now'
          ? 'in a minute'
          : new Date(at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
      const out = when === 'now' ? whenWords : `on ${whenWords}`;
      showInkToast(
        when === 'draft'
          ? 'Saved. It’s back in Drafts.'
          : saved?.updatePendingSocialPost?.itemCheck?.holds
            ? `Saved. ${KEY_HELD_TOAST}`
            : forApproval
              ? `Saved. It waits for ${approver}’s approval.`
              : draft.reachStatus === 'waiting'
                ? `Saved and approved. It goes out ${out}.`
                : draft.reachStatus === 'missed'
                  ? `Saved. Trying again ${out}.`
                  : `Saved. Still a ${KIND_WORDS[kind as PostKind]} on ${channelName(network)}, ${when === 'now' ? out : whenWords}.`
      );
      onPosted();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  const post = async () => {
    if (!accountId || !canPost) return;
    // Sent for approval: nothing goes out yet, so there's nothing to confirm.
    if (when !== 'draft' && !forApproval) {
      const personal = going.filter((c) => isPersonalProfile(c.platform, c.profileId));
      const names = going
        .map(
          (c) =>
            `${c.profileName || c.profileUsername || channelName(c.platform)} (${channelName(c.platform)})`
        )
        .join(', ');
      const whenWords =
        when === 'now'
          ? 'now'
          : `at ${new Date(at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`;
      if (
        (when === 'now' || personal.length > 0) &&
        !(await askInk({
          title: `Post to ${names} ${whenWords}?`,
          body:
            when === 'now'
              ? videoBusy
                ? 'It posts the moment the video is ready.'
                : 'It goes out in about a minute.'
              : undefined,
          note: personal.length
            ? `${personal.length === 1 ? 'This is a personal profile' : 'Some of these are personal profiles'}: it goes out under that person’s own name.`
            : undefined,
          confirmLabel: when === 'now' ? (videoBusy ? 'Post when ready' : 'Post now') : 'Schedule',
        }))
      )
        return;
    }
    setBusy(true);
    const postType = kind;
    const scheduleTime =
      when === 'now' ? 'now' : when === 'later' ? String(new Date(at).getTime()) : null;
    let done = 0;
    let held = false;
    try {
      // A draft being finished: its own row is saved (and given its time), the
      // other channels picked get new posts, and the draft goes if its channel
      // was unpicked.
      const own = draft ? going.find((c) => c.id === draft.socialProfileId) || null : null;
      if (draft && own) {
        const ownNetwork = networkKey(own.platform);
        const ownBody = textFor(ownNetwork).trim();
        await updateDraft({
          variables: {
            id: draft.id,
            input: {
              text: ownBody,
              title: titleFor(ownNetwork, ownBody),
              linkUrl: sentLink.trim() || null,
              ...postMedia(),
              tiktok: tiktokFor(ownNetwork),
              postType,
              campaignId: campaignId || null,
            },
          },
        });
        if (when !== 'draft') {
          const { data: placed } = await scheduleDraft({
            variables: {
              id: draft.id,
              scheduleTime: when === 'now' ? 'now' : new Date(at).toISOString(),
            },
          });
          held = held || Boolean(placed?.scheduleSocialPostDraft?.itemCheck?.holds);
        }
        done += 1;
      }
      for (const c of going) {
        if (own && c.id === own.id) continue;
        const network = networkKey(c.platform);
        const body = textFor(network).trim();
        const media = postMedia();
        const { data: created } = await createPost({
          variables: {
            input: {
              accountId,
              socialProfileId: c.id,
              title: titleFor(network, body),
              summary: body,
              postUrl: sentLink.trim() || null,
              imageUrl: media.mediaUrl,
              mediaAssetId: media.mediaAssetId,
              tiktok: tiktokFor(network),
              postType,
              scheduleTime,
              isDraft: when === 'draft',
              campaignId: campaignId || null,
            },
          },
        });
        held = held || Boolean(created?.createSocialPost?.itemCheck?.holds);
        done += 1;
      }
      if (draft && !own) await discardDraft({ variables: { id: draft.id } });
      showInkToast(
        held
          ? KEY_HELD_TOAST
          : forApproval && when !== 'draft'
            ? `Sent to ${approver} for approval.`
            : draft && when === 'draft'
              ? 'Draft saved.'
              : when === 'draft'
                ? `Saved as ${done === 1 ? 'a draft' : `${done} drafts`}.`
                : when === 'now'
                  ? videoBusy
                    ? `Queued. Posts to ${joinNames(going.map((c) => channelName(c.platform)))} as soon as the video is ready.`
                    : `Posting to ${done} ${done === 1 ? 'channel' : 'channels'} in a minute.`
                  : `Scheduled on ${done} ${done === 1 ? 'channel' : 'channels'}.`
      );
      onPosted();
    } catch (err) {
      showInkToast(
        (err instanceof Error ? err.message : 'That didn’t work.') +
          (done ? ` (${done} already created.)` : '')
      );
      if (done) onPosted();
    } finally {
      setBusy(false);
    }
  };

  const deleteDraft = async () => {
    if (
      !draft ||
      !(await askInk({
        title: 'Delete this draft?',
        body: 'It was never posted.',
        confirmLabel: 'Delete draft',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    try {
      await discardDraft({ variables: { id: draft.id } });
      showInkToast('Draft deleted.');
      onPosted();
    } catch (err) {
      showInkToast(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  // Feed images the site's articles show by address: pickable too (not stored in Media).
  const linked = useLinkedMedia(imageMode === 'library' ? accountId : null, libQ, 24);
  const libAssets = (libData?.mediaAssets?.assets || []).filter(
    (a) =>
      currentUrl(a) &&
      (!libQ.trim() || (a.originalFilename || '').toLowerCase().includes(libQ.trim().toLowerCase()))
  );

  // The composer's own fields and buttons: the modal frames them with the
  // channels and the preview; the side panel shows them alone (variant panel).
  const fields = (
    <>
      <div className="ink-site-label">
        <span id="wp-kind-label">Post as</span>
        <div className="wp-kinds" role="radiogroup" aria-labelledby="wp-kind-label">
          {POST_KINDS.map((k) => {
            const refused = networks.some((n) => !takes(rulesFor(n), k.kind));
            const on = kind === k.kind;
            return (
              <button
                key={k.kind}
                type="button"
                role="radio"
                aria-checked={on}
                className="wp-kind"
                data-on={on ? 'true' : undefined}
                data-refused={refused ? 'true' : undefined}
                onClick={() => setKind(k.kind)}
              >
                <span className="wp-kind-name">{k.label}</span>
                <span className="wp-kind-sub">{refused ? 'Not supported' : k.description}</span>
              </button>
            );
          })}
        </div>
        {typeFix ? (
          <div className="wp-kind-note">
            <span>{typeFix.note}</span>
            <button
              type="button"
              className="ink-link-btn"
              onClick={() => setKind(typeFix.switchTo)}
            >
              {typeFix.switchLabel}
            </button>
            {!editing ? (
              <button
                type="button"
                className="ink-quiet-link"
                onClick={() => leaveOut(unsupported)}
              >
                {typeFix.leaveLabel}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      {kind === 'photo' ? (
        <div className="ink-site-label">
          <span>Photo</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => {
              void upload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
          {image ? (
            <div className="wp-img">
              <img src={image.url} alt="" />
              <div className="wp-img-text">
                <div>{image.name}</div>
                <div className="ink-muted">{image.from}</div>
              </div>
              <button
                type="button"
                className="ink-quiet-link"
                onClick={() => setImageMode('library')}
              >
                Change
              </button>
              <button type="button" className="ink-quiet-link" onClick={() => setImage(null)}>
                Remove
              </button>
            </div>
          ) : imageMode === 'url' ? (
            <div className="wp-img-url">
              <input
                className="ink-site-input"
                type="url"
                value={imageUrl}
                autoFocus
                placeholder="https://example.com/photo.jpg"
                aria-label="Image address"
                onChange={(e) => setImageUrl(e.target.value)}
              />
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                disabled={!/^https?:\/\/\S+$/i.test(imageUrl.trim())}
                onClick={() => {
                  setImage({
                    url: imageUrl.trim(),
                    name: imageUrl.trim().split('/').pop() || 'Image',
                    from: 'From an address',
                  });
                  setImageMode('none');
                }}
              >
                Use image
              </button>
              <button type="button" className="ink-quiet-link" onClick={() => setImageMode('none')}>
                Cancel
              </button>
            </div>
          ) : imageMode !== 'library' ? (
            <div className="wp-img-empty">
              <button
                type="button"
                className="ink-btn ink-btn-primary"
                onClick={() => setImageMode('library')}
              >
                Choose from Media
              </button>
              <button
                type="button"
                className="ink-btn"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
              <button type="button" className="ink-link-btn" onClick={() => setImageMode('url')}>
                Use an image address
              </button>
            </div>
          ) : null}
          {imageMode === 'library' ? (
            <div className="wp-lib">
              <div className="wp-lib-head">
                <span>Media library</span>
                <input
                  className="ink-site-input"
                  value={libQ}
                  placeholder="Search media"
                  aria-label="Search media"
                  onChange={(e) => setLibQ(e.target.value)}
                />
                <button
                  type="button"
                  className="ink-btn"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? 'Uploading…' : 'Upload'}
                </button>
                <button
                  type="button"
                  className="ink-quiet-link"
                  aria-label="Close media library"
                  onClick={() => setImageMode('none')}
                >
                  ×
                </button>
              </div>
              <div className="wp-lib-grid">
                {libAssets.map((a) => (
                  <button key={a.id} type="button" onClick={() => pickAsset(a, 'From Media')}>
                    <img src={currentUrl(a) || ''} alt="" />
                    <span>{a.originalFilename || 'Image'}</span>
                  </button>
                ))}
                {linked.items.map((m) => (
                  <button
                    key={m.url}
                    type="button"
                    onClick={() => {
                      setImage({
                        url: m.url,
                        name: m.latestTitle || m.url.split('/').pop() || 'Image',
                        from: 'Linked from your feeds',
                      });
                      setImageMode('none');
                    }}
                  >
                    <img src={m.url} alt="" loading="lazy" referrerPolicy="no-referrer" />
                    <span>{m.latestTitle || 'Feed image'}</span>
                  </button>
                ))}
                {!libAssets.length && !linked.items.length ? (
                  <span className="ink-muted">
                    {libLoading ? 'Loading…' : libQ ? 'Nothing matches.' : 'Nothing in Media yet.'}
                  </span>
                ) : null}
              </div>
              <div className="wp-lib-foot ink-muted">
                Uploads here are added to Media too, so you can reuse them.
              </div>
            </div>
          ) : null}
          {imageError ? (
            <span className="aoe-help" style={{ color: 'var(--ink-red)' }}>
              {imageError}
            </span>
          ) : null}
        </div>
      ) : null}

      {isVideo && accountId ? (
        <VideoField
          accountId={accountId}
          video={video}
          onChange={setVideo}
          minutesLeft={minutesLeft}
          onUploaded={() => void refetchAllowance()}
          checks={checks}
          onLeaveOut={editing ? null : (key) => leaveOut([key])}
        />
      ) : null}

      {usesLink ? (
        <div className="ink-site-label">
          {kind === 'link' ? (
            <span>Link</span>
          ) : (
            <span>
              Link in the text <span className="ink-muted">(optional)</span>
            </span>
          )}
          <LinkPicker
            link={link}
            target={target}
            offSite={offSite}
            describedBy={offSite ? 'wp-offsite' : undefined}
            onPick={pickLink}
            onUseUrl={(url) => {
              setLink(url);
              setTarget(null);
            }}
            onRemove={() => {
              setLink('');
              setTarget(null);
            }}
            onClear={() => {
              setLink('');
              setTarget(null);
            }}
          />
          {offSite ? (
            <span id="wp-offsite" className="wp-offsite">
              This links to {offSite}, not your site. Readers who click leave{' '}
              {hostData?.reachSiteHost}. Link to the article on your site instead.
            </span>
          ) : null}
          <span className="ink-muted aoe-help">
            {kind === 'link'
              ? 'Shows as a card with the page’s share image, title and address. To show your own image instead, use Photo.'
              : kind === 'photo'
                ? 'Added to the end of the text. On Facebook it goes in the first comment instead.'
                : 'Added to the end of the caption. On Facebook it goes in the first comment; not clickable on Instagram or TikTok.'}
          </span>
        </div>
      ) : null}

      {needsTitle ? (
        <div className="ink-site-label">
          <label htmlFor="wp-yt-title" className="wp-net-label">
            <span className="rc-mark rc-mark-sm">{channelMark('youtube')}</span>
            YouTube title
            <span
              className="wp-net-count"
              data-over={[...youtubeTitle].length > 100 ? 'true' : undefined}
            >
              {[...youtubeTitle].length} / 100
            </span>
          </label>
          <input
            id="wp-yt-title"
            className="ink-site-input"
            value={youtubeTitle}
            maxLength={100}
            placeholder="What the video is called on YouTube"
            onChange={(e) => setYoutubeTitle(e.target.value)}
          />
        </div>
      ) : null}

      <div className="ink-site-label">
        <label htmlFor="wp-text">
          {kind === 'photo' || isVideo ? 'Caption' : 'Text'}{' '}
          {isVideo && networks.includes('youtube') ? (
            <span className="ink-muted">· the YouTube description</span>
          ) : kind === 'photo' || isVideo ? (
            <span className="ink-muted">(optional)</span>
          ) : null}
        </label>
        <textarea
          id="wp-text"
          className="ink-site-input"
          rows={4}
          value={text}
          maxLength={5000}
          placeholder={kind === 'photo' || isVideo ? 'Caption' : 'Text'}
          onChange={(e) => setText(e.target.value)}
        />
        {fine.length ? (
          <div className="wp-counts" aria-live="polite">
            {fine.map((k) => {
              const lim = postLimit(k);
              const n = lengthOf(k);
              return (
                <span key={k} style={{ color: TONE_COLOR[lengthTone(n, lim)] }}>
                  <span className="rc-mark rc-mark-sm">{channelMark(k)}</span>
                  {n.toLocaleString()}
                  {lim != null ? ` / ${lim.toLocaleString()}` : ''}
                </span>
              );
            })}
          </div>
        ) : null}
        {tooLong
          .filter((k) => versions[k] === undefined)
          .map((k) => (
            <div key={k} className="wp-over">
              <span>
                {(lengthOf(k) - (postLimit(k) || 0)).toLocaleString()} characters too long for{' '}
                {channelName(k)}.
              </span>
              <button
                type="button"
                onClick={() =>
                  setVersions((v) => ({ ...v, [k]: fitText(k, text, sentLink, lengthContext) }))
                }
              >
                Write {channelName(k) === 'X' ? 'an' : 'a'} {channelName(k)} version
              </button>
            </div>
          ))}
        {Object.keys(versions)
          .filter((k) => networks.includes(k))
          .map((k) => {
            const lim = postLimit(k);
            const n = lengthOf(k);
            return (
              <div key={k} className="wp-version">
                <div className="wp-version-head">
                  <span>{channelName(k)} version</span>
                  <span style={{ color: TONE_COLOR[lengthTone(n, lim)] }}>
                    {n.toLocaleString()}
                    {lim != null ? ` / ${lim.toLocaleString()}` : ''}
                  </span>
                  <button
                    type="button"
                    onClick={() =>
                      setVersions((v) => {
                        const { [k]: _gone, ...rest } = v;
                        return rest;
                      })
                    }
                  >
                    Use the main text
                  </button>
                </div>
                <textarea
                  className="ink-site-input"
                  rows={3}
                  value={versions[k]}
                  aria-label={`${channelName(k)} version`}
                  onChange={(e) => setVersions((v) => ({ ...v, [k]: e.target.value }))}
                />
              </div>
            );
          })}
      </div>

      {needsTiktok ? (
        <div className="wp-tiktok">
          <div className="wp-net-label">
            <span className="rc-mark rc-mark-sm">{channelMark('tiktok')}</span>
            <strong>TikTok settings</strong>
            <span className="ink-muted">
              Posting as{' '}
              {tiktokChannel?.profileUsername
                ? `@${tiktokChannel.profileUsername.replace(/^@/, '')}`
                : tiktokChannel?.profileName || 'your account'}
            </span>
          </div>
          <label className="ink-site-label">
            Who can see it
            <select
              className="ink-site-input wp-tiktok-privacy"
              data-unset={tiktok.privacy ? undefined : 'true'}
              value={tiktok.privacy}
              onChange={(e) => setTiktok((t) => ({ ...t, privacy: e.target.value }))}
            >
              <option value="">Choose…</option>
              <option value="public">Everyone</option>
              <option value="friends">Friends</option>
              <option value="self">Only you</option>
            </select>
          </label>
          <div className="wp-tiktok-toggles">
            {(
              [
                ['allowComments', 'Allow comments'],
                ['allowDuet', 'Allow Duet'],
                ['allowStitch', 'Allow Stitch'],
              ] as Array<['allowComments' | 'allowDuet' | 'allowStitch', string]>
            ).map(([key, label]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={tiktok[key]}
                  onChange={(e) => setTiktok((t) => ({ ...t, [key]: e.target.checked }))}
                />
                {label}
              </label>
            ))}
          </div>
          <span className="ink-muted aoe-help">
            TikTok asks for this on every post. By posting, you agree to TikTok’s Music Usage
            Confirmation.
          </span>
        </div>
      ) : null}

      <div className="aoe-two">
        <div className="ink-site-label">
          <label htmlFor="wp-campaign">Campaign</label>
          <select
            id="wp-campaign"
            className="ink-site-input"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
          >
            <option value="">None</option>
            {campaigns.map((c) => {
              const { start, end } = campaignWindow(c);
              return (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {end ? ` · ${shortDate(start)} – ${shortDate(end)}` : ''}
                </option>
              );
            })}
          </select>
        </div>
        {
          <div className="ink-site-label">
            When
            <span className="ink-seg" style={{ alignSelf: 'flex-start' }}>
              {(
                [
                  ['now', 'Now'],
                  ['later', 'Pick a time'],
                  ['draft', 'Save as draft'],
                ] as Array<[When, string]>
              )
                .filter(([key]) => !forApproval || key !== 'now')
                .map(([key, label]) => (
                  <button
                    key={key}
                    type="button"
                    data-active={when === key}
                    onClick={() => setWhen(key)}
                  >
                    {label}
                  </button>
                ))}
            </span>
          </div>
        }
      </div>
      {when === 'later' ? (
        <div className="wp-when">
          <input
            className="ink-site-input"
            type="date"
            value={at.slice(0, 10)}
            min={localInputValue(new Date()).slice(0, 10)}
            onChange={(e) => e.target.value && setAt(`${e.target.value}T${at.slice(11, 16)}`)}
            aria-invalid={!laterOk}
            aria-label="Day it goes out"
          />
          <input
            className="ink-site-input"
            type="time"
            value={at.slice(11, 16)}
            onChange={(e) => e.target.value && setAt(`${at.slice(0, 10)}T${e.target.value}`)}
            aria-invalid={!laterOk}
            aria-label="Time it goes out"
            aria-describedby="wp-zone"
          />
          <span className="ink-muted aoe-help" id="wp-zone">
            Your time · {zoneCity()}
          </span>
        </div>
      ) : when === 'draft' ? (
        <span className="ink-muted aoe-help">
          Nothing goes out. It waits under Drafts on the Calendar.
        </span>
      ) : null}
      {campaignHint ? (
        <span className="aoe-help" style={{ color: 'var(--ink-amber)' }}>
          {campaignHint}
        </span>
      ) : null}
    </>
  );
  const actions = (
    <>
      {draft && !editing && variant === 'modal' ? (
        <button
          type="button"
          className="ink-quiet-link wp-del"
          disabled={busy}
          onClick={() => void deleteDraft()}
        >
          Delete draft
        </button>
      ) : null}
      {/* Always there: it holds Delete draft to the left, buttons to the right. */}
      <span className={block ? 'wp-block wp-block-stop' : 'ink-muted wp-block'}>{footNote}</span>
      <button type="button" className="ink-btn ink-btn-quiet" disabled={busy} onClick={onClose}>
        Cancel
      </button>
      <button
        type="button"
        className="ink-btn ink-btn-primary"
        disabled={!canPost}
        onClick={() => void (editing ? saveEdit() : post())}
      >
        {editing
          ? busy
            ? 'Saving…'
            : when === 'now'
              ? 'Post now'
              : 'Save changes'
          : busy
            ? when === 'draft'
              ? 'Saving…'
              : when === 'now'
                ? 'Posting…'
                : 'Scheduling…'
            : when === 'draft'
              ? draft
                ? 'Keep as draft'
                : 'Save draft'
              : when === 'now'
                ? videoBusy
                  ? 'Post when ready'
                  : 'Post now'
                : forApproval
                  ? 'Send for approval'
                  : 'Schedule'}
      </button>
    </>
  );

  // Reach side panel → Edit (UX optimization review, 2026-10-24): an existing
  // post is edited in the panel with the composer's fields; its channel stays.
  if (variant === 'panel') {
    return (
      <>
        <div className="rc-panel-body wp-in-panel">
          <div className="aoe-form wp-form">{fields}</div>
        </div>
        <div className="rc-panel-foot wp-panel-foot">{actions}</div>
      </>
    );
  }

  return (
    <div
      className="ink-modal-back"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div
        className="ink-modal wp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wp-title"
      >
        <div className="wp-head">
          <div style={{ minWidth: 0, flex: 1 }}>
            <div className="wp-title" id="wp-title">
              {editing ? 'Edit post' : draft ? 'Draft' : 'Write a post'}
            </div>
            <p className="wp-sub">
              {forApproval
                ? `${approver} approves it before it goes out.${
                    editing && draft?.reachStatus !== 'waiting'
                      ? ' A change sends it back for approval.'
                      : ''
                  }`
                : editing && editChannel
                  ? [
                      draft?.reachStatus === 'scheduled' && editTime
                        ? `Scheduled · ${editTime.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`
                        : '',
                      kind ? KIND_WORDS[kind] : '',
                      channelName(editChannel.platform),
                      editChannel.profileName ||
                        editChannel.profileUsername ||
                        channelName(editChannel.platform),
                      fromRule ? `from ${fromRule}` : '',
                      draft?.reachStatus === 'waiting' ? 'saving approves it' : '',
                    ]
                      .filter(Boolean)
                      .join(' · ')
                  : draft
                    ? 'Finish it, give it a time, or keep it as a draft.'
                    : 'You approve your own posts, so they go straight on the calendar.'}
            </p>
          </div>
          <button
            type="button"
            className="wp-close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <div className="wp-grid">
          <div className="aoe-form wp-form">
            <div className="ink-site-label">
              Post to
              {channels.length ? (
                <div className="wp-chans">
                  {/* An edit stays on its own channel. */}
                  {channels
                    .filter((c) => !editing || c.id === draft?.socialProfileId)
                    .map((c) => {
                      const net = networkKey(c.platform);
                      const on = chosen.has(c.id);
                      const why = on
                        ? channelProblem(net, unsupported.includes(net), videoFails.includes(net))
                        : '';
                      return (
                        <label
                          className="wp-chan"
                          key={c.id}
                          data-on={on ? 'true' : undefined}
                          data-problem={why ? 'true' : undefined}
                          data-locked={editing ? 'true' : undefined}
                          data-off={publishingOff(c) ? 'true' : undefined}
                        >
                          <input
                            type="checkbox"
                            className="wp-chan-box"
                            checked={chosen.has(c.id)}
                            disabled={publishingOff(c) && !chosen.has(c.id)}
                            aria-disabled={editing}
                            onChange={() => {
                              if (!editing) toggle(c.id);
                            }}
                          />
                          <span className="rc-mark rc-mark-sm">{channelMark(c.platform)}</span>
                          <span className="chp-name">
                            <span>
                              {c.profileName || c.profileUsername || channelName(c.platform)}
                            </span>
                            <span className={why ? 'wp-chan-why' : 'ink-muted'}>
                              {channelName(c.platform)} ·{' '}
                              {why ||
                                (publishingOff(c)
                                  ? PUBLISHING_OFF_WORDS
                                  : channelKind(c.platform, c.profileId))}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                </div>
              ) : (
                <span className="ink-muted">No channels connected yet.</span>
              )}
            </div>
            {fields}
          </div>
          <div className="wp-pv-pane">
            <PostPreview
              channels={going}
              kind={kind}
              textFor={textFor}
              link={link}
              linkTitle={target?.title}
              imageUrl={image?.url || null}
              video={video}
              youtubeTitle={youtubeTitle}
              rulesFor={rulesFor}
              campaign={selectedAccount?.utmCampaign}
            />
          </div>
        </div>
        <div className="ink-modal-actions wp-foot">{actions}</div>
      </div>
    </div>
  );
}
